import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import api from '../../lib/api';
import useAdminPageMeta from '../../hooks/useAdminPageMeta';
import { renderBlock } from '../../components/PageRenderer';
import {
  ChevronUp, ChevronDown, Trash2, Save, ArrowLeft, Plus, Eye, Edit3, Layers,
  Settings, AlertTriangle, X, LayoutGrid,
} from 'lucide-react';

const BLOCK_LIBRARY = {
  Sections: [
    { type: 'section_hero', label: 'Hero', defaults: { tagline: 'A new tagline', title: 'New Hero Title', subtitle: 'Subtitle here', button_text: 'Get Started', button_url: '/', price_note: '', image_src: '' } },
    { type: 'section_social_proof', label: 'Social Proof', defaults: { stats: [{ number: '1K+', label: 'Users' }] } },
    { type: 'section_pillars', label: 'Pillars', defaults: { section_label: 'PILLARS', title: 'Title', subtitle: 'Subtitle', items: [{ title: 'Pillar 1', subtitle: '...', color: '#c4a144', icon: 'sparkles' }] } },
    { type: 'section_about', label: 'About', defaults: { section_label: 'ABOUT', title: 'About', paragraphs: ['Lorem ipsum'], author_name: 'Author', author_role: 'Role', author_initial: 'A', quote_text: 'Quote', quote_author: 'Source' } },
    { type: 'section_features', label: 'Features', defaults: { section_label: 'FEATURES', title: 'Features', subtitle: '', items: [{ icon: 'sparkles', title: 'Feature', description: 'Description' }] } },
    { type: 'section_ee', label: 'EE Showcase', defaults: { section_label: 'EE', title: 'Meet EE', description: 'Description', bullets: ['Benefit 1'], chat_user_msg: 'Hello', chat_ee_msg: 'Hi there' } },
    { type: 'section_testimonials', label: 'Testimonials', defaults: { section_label: 'PRAISE', title: 'Testimonials', items: [{ quote: 'Great', author: 'Reader', role: 'Customer' }] } },
    { type: 'section_pricing', label: 'Pricing', defaults: { section_label: 'PRICING', title: 'Get It', badge_text: 'POPULAR', amount: '$49', amount_label: 'one-time', features: [{ icon: 'check-circle', text: 'Feature 1' }], cta_text: 'Buy', cta_url: '/buy', guarantee_text: '30-day guarantee' } },
    { type: 'section_footer', label: 'Footer', defaults: { brand_name: 'Brand', brand_tagline: 'Tagline', link_columns: [{ title: 'Links', links: ['Home', 'About'] }], copyright: '© 2025' } },
  ],
  Text: [
    { type: 'paragraph', label: 'Paragraph', defaults: { text: 'New paragraph text…' } },
    { type: 'heading', label: 'Heading', defaults: { level: 2, text: 'New Heading' } },
    { type: 'list', label: 'List', defaults: { items: ['Item 1', 'Item 2'] } },
    { type: 'quote', label: 'Quote', defaults: { text: 'A beautiful quote.', author: 'Author' } },
    { type: 'image', label: 'Image', defaults: { src: '', alt: '' } },
    { type: 'button', label: 'Button', defaults: { label: 'Click me', url: '/' } },
    { type: 'divider', label: 'Divider', defaults: {} },
    { type: 'spacer', label: 'Spacer', defaults: { size: 40 } },
  ],
};

function newId() { return 'b_' + Math.random().toString(36).slice(2, 10); }

// Snapshot helper for dirty checking.
const snapshot = (s) => JSON.stringify(s);

export default function PageEditorPage() {
  const [sp] = useSearchParams();
  const id = sp.get('id');
  const nav = useNavigate();

  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [meta, setMeta] = useState({ title: '', slug: '', meta_description: '' });
  const [status, setStatus] = useState('draft');

  const [selected, setSelected] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false); // mobile drawer
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  // Snapshot used for dirty detection.
  const baselineRef = useRef(null);

  // Branded admin title: e.g. "Editing 'Home' · Control Center | The Elemental Embrace"
  useAdminPageMeta(page ? `Editing "${meta.title || page.title || page.slug}"` : 'Page Editor');

  useEffect(() => {
    if (!id) return;
    api.get(`/api/admin/pages/${id}`).then(({ data }) => {
      setPage(data);
      setBlocks(data.blocks || []);
      const m = {
        title: data.title || '',
        slug: data.slug || '',
        meta_description: data.meta_description || '',
      };
      setMeta(m);
      const st = data.status || 'draft';
      setStatus(st);
      baselineRef.current = snapshot({ blocks: data.blocks || [], meta: m, status: st });
    }).catch((e) => setErr(e.message));
  }, [id]);

  const currentSnapshot = useMemo(
    () => snapshot({ blocks, meta, status }),
    [blocks, meta, status]
  );
  const isDirty = baselineRef.current != null && currentSnapshot !== baselineRef.current;

  // ── Unsaved-changes guards ────────────────────────────────────────────────
  // 1) Browser refresh/close
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required for Chrome
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // 2) In-app navigation (react-router v7)
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) =>
        isDirty && currentLocation.pathname !== nextLocation.pathname,
      [isDirty]
    )
  );

  // Block actions
  const updateBlock = (idx, patch) => setBlocks((bs) => bs.map((b, i) => i === idx ? { ...b, ...patch } : b));
  const moveBlock = (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= blocks.length) return;
    const copy = [...blocks];
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    setBlocks(copy);
    setSelected(j);
  };
  const removeBlock = (idx) => { setBlocks((bs) => bs.filter((_, i) => i !== idx)); setSelected(null); };
  const addBlock = (spec) => {
    const newBlock = { block_id: newId(), type: spec.type, ...spec.defaults };
    setBlocks((bs) => [...bs, newBlock]);
    setSelected(blocks.length);
    setLibraryOpen(false); // close mobile drawer after picking
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return setDragIdx(null);
    const copy = [...blocks];
    const [moved] = copy.splice(dragIdx, 1);
    copy.splice(i, 0, moved);
    setBlocks(copy); setDragIdx(null); setSelected(i);
  };

  const save = async (newStatus) => {
    setSaving(true); setErr(null);
    try {
      const payload = {
        blocks,
        title: meta.title,
        meta_description: meta.meta_description,
      };
      // Only send slug if changed AND not the protected `home` page
      if (page && meta.slug && meta.slug !== page.slug && page.slug !== 'home') {
        payload.slug = meta.slug;
      }
      if (newStatus) payload.status = newStatus;
      await api.put(`/api/admin/pages/${id}`, payload);
      if (newStatus) setStatus(newStatus);
      setSavedAt(new Date());
      // Reset baseline so isDirty becomes false
      baselineRef.current = snapshot({
        blocks,
        meta,
        status: newStatus || status,
      });
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  if (!id) return <div className="p-10 text-ee-muted">No page id provided.</div>;
  if (err && !page) return <div className="p-10 text-red-400">{err}</div>;
  if (!page) return <div className="p-10 text-ee-muted">Loading…</div>;

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] lg:h-screen overflow-hidden">
      {/* Block library — DESKTOP (always visible) */}
      <aside className="hidden lg:flex w-64 bg-ee-bg2 border-r border-ee-border flex-col overflow-y-auto flex-shrink-0">
        <div className="p-4 border-b border-ee-border">
          <button onClick={() => nav('/admin/pages')} className="text-xs text-ee-muted hover:text-gold inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to pages</button>
          <div className="text-white font-serif text-lg mt-2 truncate">{meta.title || page.title}</div>
          <div className="text-xs text-ee-muted font-mono">/{meta.slug || page.slug}</div>
        </div>
        {Object.entries(BLOCK_LIBRARY).map(([cat, items]) => (
          <div key={cat} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-gold/80 mb-2 px-1">{cat}</div>
            <div className="space-y-1">
              {items.map((it) => (
                <button key={it.type} onClick={() => addBlock(it)} className="w-full text-left text-sm px-3 py-2 rounded-md text-ee-text2 hover:bg-ee-card2 hover:text-white border border-transparent hover:border-gold/30 transition-colors flex items-center gap-2">
                  <Plus size={12} className="text-gold" /> {it.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </aside>

      {/* Block library — MOBILE DRAWER */}
      {libraryOpen && (
        <>
          <button
            aria-label="Close blocks panel"
            onClick={() => setLibraryOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          />
          <aside
            role="dialog"
            aria-modal="true"
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-ee-bg2 border-r border-ee-border flex flex-col overflow-y-auto animate-slide-in"
          >
            <div className="p-4 border-b border-ee-border flex items-start justify-between gap-2">
              <div className="min-w-0">
                <button onClick={() => { setLibraryOpen(false); nav('/admin/pages'); }} className="text-xs text-ee-muted hover:text-gold inline-flex items-center gap-1"><ArrowLeft size={12} /> Back to pages</button>
                <div className="text-white font-serif text-lg mt-2 truncate">{meta.title || page.title}</div>
                <div className="text-xs text-ee-muted font-mono truncate">/{meta.slug || page.slug}</div>
              </div>
              <button onClick={() => setLibraryOpen(false)} aria-label="Close" className="p-1 -mr-1 rounded text-ee-muted hover:text-white hover:bg-ee-card2 flex-shrink-0">
                <X size={18} />
              </button>
            </div>
            {Object.entries(BLOCK_LIBRARY).map(([cat, items]) => (
              <div key={cat} className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-gold/80 mb-2 px-1">{cat}</div>
                <div className="space-y-1">
                  {items.map((it) => (
                    <button key={it.type} onClick={() => addBlock(it)} className="w-full text-left text-sm px-3 py-2 rounded-md text-ee-text2 hover:bg-ee-card2 hover:text-white border border-transparent hover:border-gold/30 transition-colors flex items-center gap-2">
                      <Plus size={12} className="text-gold" /> {it.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </aside>
        </>
      )}

      {/* Center: Editor */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-3 sm:px-5 py-3 border-b border-ee-border bg-ee-bg2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              data-testid="open-block-library"
              onClick={() => setLibraryOpen(true)}
              className="lg:hidden p-1.5 rounded border border-ee-border text-ee-text2 hover:text-gold hover:border-gold/40"
              aria-label="Open block library"
            >
              <LayoutGrid size={14} />
            </button>
            <Layers size={16} className="text-gold flex-shrink-0 hidden sm:inline" />
            <span className="text-white text-sm font-medium">Editor</span>
            <span className="text-xs text-ee-muted hidden sm:inline">{blocks.length} blocks</span>
            {isDirty
              ? <span className="text-xs text-yellow-400 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-400" /> Unsaved</span>
              : savedAt && <span className="text-xs text-green-400 hidden sm:inline">Saved {savedAt.toLocaleTimeString()}</span>
            }
            {err && <span className="text-xs text-red-400 truncate max-w-[140px]">{err}</span>}
          </div>
          <div className="flex items-center flex-wrap gap-2">
            <button
              data-testid="page-settings"
              onClick={() => setShowSettings((s) => !s)}
              className={`text-xs px-2.5 sm:px-3 py-1.5 rounded border inline-flex items-center gap-1 ${showSettings ? 'border-gold/60 text-gold' : 'border-ee-border text-ee-text2 hover:text-gold hover:border-gold/40'}`}
            >
              <Settings size={12} /> SEO
            </button>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-ee-bg border border-ee-border rounded-md px-2 py-1 text-xs text-white">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <button onClick={() => setPreviewMode((p) => !p)} className="text-xs px-2.5 sm:px-3 py-1.5 rounded border border-ee-border text-ee-text2 hover:text-gold hover:border-gold/40 inline-flex items-center gap-1">
              {previewMode ? <><Edit3 size={12} /> Edit</> : <><Eye size={12} /> Preview</>}
            </button>
            <button data-testid="save-page" onClick={() => save(status)} disabled={saving || !isDirty} className="text-xs px-3 sm:px-4 py-1.5 rounded gold-grad text-black font-semibold inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </header>

        {/* SEO / Page settings drawer */}
        {showSettings && (
          <div className="border-b border-ee-border bg-ee-bg2/60 px-3 sm:px-5 py-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gold/80">Title</label>
                <input
                  value={meta.title}
                  onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
                  className="w-full mt-1 bg-ee-bg border border-ee-border rounded-md px-3 py-1.5 text-sm text-white"
                  placeholder="Page title"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gold/80">
                  Slug {page.slug === 'home' && <span className="text-ee-muted normal-case tracking-normal">(locked for Home)</span>}
                </label>
                <input
                  value={meta.slug}
                  disabled={page.slug === 'home'}
                  onChange={(e) =>
                    setMeta((m) => ({
                      ...m,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, ''),
                    }))
                  }
                  className="w-full mt-1 bg-ee-bg border border-ee-border rounded-md px-3 py-1.5 text-sm text-white font-mono disabled:opacity-60"
                  placeholder="my-page"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-[10px] uppercase tracking-wider text-gold/80">
                  Meta description <span className="text-ee-muted normal-case tracking-normal">({meta.meta_description.length}/160)</span>
                </label>
                <textarea
                  value={meta.meta_description}
                  onChange={(e) => setMeta((m) => ({ ...m, meta_description: e.target.value.slice(0, 200) }))}
                  rows={2}
                  className="w-full mt-1 bg-ee-bg border border-ee-border rounded-md px-3 py-1.5 text-sm text-white"
                  placeholder="Shown in search results and social shares."
                />
              </div>
            </div>
            <p className="text-[11px] text-ee-muted mt-2">
              These values drive <code className="text-ee-text2">&lt;title&gt;</code> and Open Graph / Twitter meta tags on the public page.
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-ee-bg">
          {blocks.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center mb-4"><Plus size={28} className="text-gold" /></div>
              <div className="text-white font-serif text-xl mb-1">Start building</div>
              <div className="text-ee-muted text-sm">
                <span className="hidden lg:inline">Pick a block from the left to begin.</span>
                <span className="lg:hidden">Tap the blocks icon (top-left) to add a block.</span>
              </div>
            </div>
          )}
          {blocks.map((b, i) => (
            <div
              key={b.block_id || i}
              draggable={!previewMode}
              onDragStart={() => onDragStart(i)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(i)}
              onClick={() => !previewMode && setSelected(i)}
              className={`relative group ${selected === i && !previewMode ? 'block-selected' : ''}`}
            >
              {!previewMode && (
                <div className="absolute top-2 right-2 z-20 hidden group-hover:flex items-center gap-1 bg-ee-bg/90 border border-ee-border rounded-md p-1 backdrop-blur">
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(i, -1); }} className="p-1 text-ee-muted hover:text-gold" title="Move up"><ChevronUp size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(i, 1); }} className="p-1 text-ee-muted hover:text-gold" title="Move down"><ChevronDown size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); removeBlock(i); }} className="p-1 text-red-400 hover:text-red-300" title="Remove"><Trash2 size={14} /></button>
                </div>
              )}
              {renderBlock(b, { mode: previewMode ? 'view' : (selected === i ? 'edit' : 'view'), onChange: (patch) => updateBlock(i, patch) })}
            </div>
          ))}
        </div>
      </div>

      {/* Unsaved-changes confirmation modal (router blocker) */}
      {blocker && blocker.state === 'blocked' && (
        <UnsavedChangesModal
          onCancel={() => blocker.reset()}
          onDiscard={() => blocker.proceed()}
          onSave={async () => {
            await save(status);
            blocker.proceed();
          }}
          saving={saving}
        />
      )}
    </div>
  );
}

function UnsavedChangesModal({ onCancel, onDiscard, onSave, saving }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="ee-card w-full max-w-md p-6 relative">
        <button onClick={onCancel} className="absolute top-3 right-3 text-ee-muted hover:text-white" aria-label="Close"><X size={18} /></button>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-yellow-500/10 border border-yellow-500/40 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={18} className="text-yellow-400" />
          </div>
          <div>
            <h3 className="text-white font-serif text-lg leading-tight">You have unsaved changes</h3>
            <p className="text-ee-muted text-sm mt-1">If you leave now, your edits to this page will be lost.</p>
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 mt-5">
          <button onClick={onCancel} className="px-3 py-2 rounded border border-ee-border text-ee-text2 text-sm hover:text-white">Stay & keep editing</button>
          <button onClick={onDiscard} className="px-3 py-2 rounded border border-red-500/40 text-red-300 hover:text-red-200 text-sm">Discard changes</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded gold-grad text-black font-semibold text-sm inline-flex items-center gap-1 disabled:opacity-60">
            <Save size={14} /> {saving ? 'Saving…' : 'Save & continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
